import { fetchApiGraphQL } from '@/utils/fetcher';
import { getUserAffiliations } from './useraffiliationstore';

export const userUpdatedEventName = 'userUpdated';

/* Declaring any affiliation is what makes someone a member on the website - no
   type counts for more than another, and none of them are verified here. A
   worker checks the claim by hand later in the process, before contracts.
   The logged-out case is handled inside getUserAffiliations. */
export function isMember(): boolean {
    return getUserAffiliations().length > 0;
}

export type MeUser = {
  name: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string;
  phoneNumber: string | null;
  address: string | null;
  postalCode: string | null; 
  emailVerified: boolean;
};

export type MeQueryResult = {
  data: {
    me: {
      user: MeUser | null;
    } | null;
  };
};

export const meQuery = `
  query {
    me {
      user {
        name
        firstName
        lastName
        email
        phoneNumber
        address
        postalCode
        emailVerified
      }
    }
  }
`;

const storage = localStorage;
const storageKey = 'user';

const storageEventHandler = (evt: StorageEvent) => {
    // Cross tab
    if (evt.key === storageKey) {
        notify();
    }
};

let mePromise: Promise<MeUser | null> | undefined = undefined;

export default function init() {
    globalThis.addEventListener('storage', storageEventHandler);
    getUserAsync();
}

function notify() {
    mePromise = undefined;
    document.dispatchEvent(new Event(userUpdatedEventName));
}

export function setUser(value: MeUser | null) {
    if (!value) {
        storage.removeItem(storageKey);
    } else {
        storage.setItem(storageKey, JSON.stringify(value));
    }

    notify();
}

export function getUserStored(): MeUser | null {
    const json = storage.getItem(storageKey);
    if (!json) {
        return null;
    }

    return JSON.parse(json);
}

export function refreshUserAsync(): Promise<MeUser | null> {
    setUser(null);
    return getUserInternal();
}

function getUserInternal(): Promise<MeUser | null> {
    const promise = fetchApiGraphQL<MeQueryResult>(meQuery, {}).then((result: MeQueryResult) => {
        const meUser = result?.data?.me?.user ?? null;
        setUser(meUser);
        return meUser;
    }).catch(() => {
        setUser(null);
        return null;
    });

    mePromise = promise;
    return promise;
}

export function getUserAsync(): Promise<MeUser | null> {

    if (mePromise !== undefined) {
        return mePromise;
    }

    const user = getUserStored();
    if (user) {
        mePromise = Promise.resolve(user);
        return mePromise;
    }

    return getUserInternal();
}
